import { ErrorMessage } from '@hookform/error-message';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, AlertDescription, AlertTitle } from '@repo/shadcn';
import { FormProvider, useForm } from 'react-hook-form';
import { FiAlertCircle } from 'react-icons/fi';
import { z } from 'zod';

import BtnLoader from '@components/ui/loader/btn-loader';

import { APP_LOGO_COLOR_SM } from '@constants/app.constant';

import { LoginFields } from '@/configs/auth.config';
import { useAuth } from '@/hooks';
import { LoginSchema } from '@/schemas';
import { DynamicControl } from '@/shared/dynamic-control';

export const SignInForm = () => {
    const form = useForm({
        resolver: zodResolver(LoginSchema),
        defaultValues: {
            email_or_phone: '',
            password: '',
        },
    });

    const { handleSubmit } = form;

    const { signIn, isLoading, apiError, isSuccess } = useAuth();

    async function onSubmit(
        value: z.infer<typeof LoginSchema>,
        event: React.BaseSyntheticEvent<object, any, any> | undefined,
    ) {
        event?.preventDefault();

        await signIn({
            email_or_phone: value.email_or_phone,
            password: value.password,
        });
    }

    return (
        <>
            <div className="w-full lg:flex">
                <div className="absolute left-0 top-0 h-full bg-indigo-100 shadow-xl lg:w-1/2 lg:bg-white xl:max-w-screen-sm">
                    <div className="flex justify-center py-12 lg:justify-start lg:px-12 xl:px-20">
                        <div className="flex cursor-pointer items-center">
                            <div className="ml-2 flex items-center gap-3 text-2xl font-semibold tracking-wide text-primary">
                                <img
                                    src={APP_LOGO_COLOR_SM}
                                    className="h-10 w-10"
                                    alt="logo"
                                />
                                Link-Pharmacy
                            </div>
                        </div>
                    </div>
                    <div className="mt-10 px-12 sm:px-24 md:px-48 lg:mt-16 lg:px-12 xl:max-w-2xl xl:px-24">
                        <h2 className="font-display xl:text-bold text-center text-4xl font-semibold text-primary lg:text-left xl:text-5xl">
                            Log in
                        </h2>
                        <div className="mt-12">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <FormProvider {...form}>
                                    {LoginFields.map((d, i) => (
                                        <div
                                            key={i}
                                            className={`mt-3 ${apiError && 'animate-shake'} `}
                                        >
                                            <DynamicControl
                                                {...d}
                                                className={`${
                                                    apiError?.data?.data.error
                                                        ? 'border-[1px] border-red-500'
                                                        : ''
                                                }`}
                                            />
                                            <ErrorMessage
                                                name={d.fieldName}
                                                render={({ message }) => (
                                                    <p className="mt-1 text-sm text-red-600">
                                                        {message}
                                                    </p>
                                                )}
                                            ></ErrorMessage>
                                        </div>
                                    ))}
                                </FormProvider>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="font-display focus:shadow-outline mt-6 flex w-full justify-center rounded-full bg-primary p-4 font-semibold tracking-wide text-gray-100 shadow-lg hover:bg-primary focus:outline-none disabled:bg-gray-200 disabled:text-gray-500"
                                >
                                    {isLoading ? <BtnLoader /> : 'Log In'}
                                </button>
                            </form>
                            {apiError?.data?.data.error && !isSuccess && (
                                <Alert variant="destructive" className="mt-6">
                                    <FiAlertCircle className="h-4 w-4" />
                                    <AlertTitle>
                                        {apiError.data?.data.error}
                                    </AlertTitle>
                                    <AlertDescription>
                                        {apiError.data?.data.message}
                                    </AlertDescription>
                                </Alert>
                            )}
                            <div className="font-display fmdt-semibold mt-12 text-center text-sm text-gray-600">
                                Don't have an account ?{' '}
                                <a className="cursor-pointer text-primary hover:text-primary">
                                    Sign up
                                </a>
                            </div>
                        </div>
                    </div>
                    <p className="mt-8 text-center">
                        Developed by{' '}
                        <a
                            className="font-bold text-primary"
                            href="https://intwins.com/"
                        >
                            {' '}
                            @Intwins
                        </a>
                    </p>
                </div>
                <div className="hidden h-screen w-full items-end justify-center bg-indigo-100 lg:flex">
                    <div className="ml-auto w-[60%]">
                        <div className="ml-auto w-[90%]">
                            <img src="/auth/auth.webp" alt="auth" />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
